import { Injectable, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Observable, defer, map, switchMap, throwError } from 'rxjs';
import { ENVIRONMENT } from '../../environment.token';
import { AnonymousIdService } from '../../analytics/anonymous-id.service';
import { SignupAddress } from '../address/signup-address.store';
import { ContactFormValue } from '../contact/contact-form.store';
import { DirectQuoteStore } from './direct-quote.store';

export const DOT_COM_ADDRESSES_ENDPOINT = '/addresses';
export const DOT_COM_PAGES_ENDPOINT = '/pages';
export const DIRECT_QUOTE_ROUTE = '/direct-quote';
export const ABOUT_YOU_PATH = '/quote/about-you';
export const DIRECT_QUOTE_UTM = { utm_source: 'tax_protest', utm_medium: 'referral' } as const;

/**
 * `POST /addresses` body: the street line (with city, state and zip) and the unit apart, and the UTM and session that
 * dot-com saves on the new quote
 */
export interface DotComAddressRequest {
  street_address: string;
  unit_number: string;
  utm_source: string;
  utm_medium: string;
  /** The anonymous ID, left out when the browser has none */
  session?: string;
}

/** `POST /addresses`: new customer response and its token, `redirect_to` alone when dot-com rejects the address */
export interface DotComAddressResponse {
  address?: { standardized_address: string; state: string };
  authentication?: { token: string; expires: number };
  resumable?: boolean;
  redirect_to?: string;
}

/**
 * The about-you answers, modeled on quoting-ui's about-you page: its `LegacyApiPagesCirAboutYou` payload and
 * `AboutYouComponent` (date of birth rules, consents, Segment ID).
 */
export interface DotComAboutYou {
  first_name: string;
  last_name: string;
  email: string;
  /** (555) 555-0123 */
  phone_number: string;
  /** YYYY-MM-DD */
  dob: string;
  opt_in_text: boolean;
  opt_out_gpc_tracking: boolean;
  ajs_anonymous_id: string;
  language_preference?: string;
}

export interface DotComPagesRequest {
  customerResponse: DotComAboutYou;
  path: typeof ABOUT_YOU_PATH;
}

/** `POST /pages`: the next page (possibly `loading`), or a redirect; the link doesn't wait on either */
export interface DotComPagesResponse {
  page?: { path: string; layout: string };
  redirect_to?: string;
}

/** `POST /addresses` */
export function toDotComAddress(address: SignupAddress, anonymousId: string): DotComAddressRequest {
  return {
    street_address: `${address.street} ${address.city}, ${address.state} ${address.zip}`,
    unit_number: address.unit ?? '',
    ...DIRECT_QUOTE_UTM,
    ...(anonymousId && { session: anonymousId }),
  };
}

/** What the browser adds to the about-you answers */
export interface DotComAboutYouBrowser {
  ajsAnonymousId: string;
  /** The Global Privacy Control signal (`navigator.globalPrivacyControl`); true opts the person out of data sale */
  optOutGpcTracking: boolean;
  languagePreference?: string;
}

/** Browsers without GPC leave it undefined */
type GpcNavigator = Navigator & { globalPrivacyControl?: boolean };

/** The contact responses, /enrolled's date of birth (YYYY-MM-DD) and the browser's values */
export function toDotComAboutYou(
  contact: ContactFormValue, dob: string, { ajsAnonymousId, optOutGpcTracking, languagePreference }: DotComAboutYouBrowser,
): DotComAboutYou {
  return {
    first_name: contact.firstName.trim(),
    last_name: contact.lastName.trim(),
    email: contact.email.trim(),
    phone_number: contact.phone,
    dob,
    opt_in_text: contact.marketingConsent, // `false` opts the person out of SMS texts
    opt_out_gpc_tracking: optOutGpcTracking,
    ajs_anonymous_id: ajsAnonymousId,
    ...(languagePreference && { language_preference: languagePreference }),
  };
}

/**
 * Starts a Kin home quote on dot-com from the answers in DirectQuoteStore and hands back a direct quote link that
 * loads (polls) it from quoting (QUI) through the `/direct-quote?token=` entry path.
 *
 * The chain:
 *   1. `POST /addresses` creates the customer response and its token
 *   2. `POST /pages` saves the about-you answers with it
 *   3. The token is included in the direct quote link, and quoting handles the initial question flow
 */
@Injectable({ providedIn: 'root' })
export class DirectQuoteService {
  private http = inject(HttpClient);
  private env = inject(ENVIRONMENT);
  private document = inject(DOCUMENT);
  private store = inject(DirectQuoteStore);
  private anonymousId = inject(AnonymousIdService);

  private addressesUrl = `${this.env.dotComApiUrl}${DOT_COM_ADDRESSES_ENDPOINT}`;
  private pagesUrl = `${this.env.dotComApiUrl}${DOT_COM_PAGES_ENDPOINT}`;

  /** For when a direct quote can't be started */
  readonly funnelUrl = `${this.env.quotingUrl}?${new URLSearchParams(DIRECT_QUOTE_UTM)}`;

  /**
   * Saves the store's address, contact responses and date of birth to dot-com and emits the direct quote link once
   * they're saved. Errors with no address or date of birth, without the TCPA consent (required to submit about-you),
   * when dot-com rejects the address (no token), or when `POST /pages` fails. The store is read on
   * subscribe.
   */
  createLink(): Observable<string> {
    return defer(() => {
      const answers = this.store.answers();
      const dob = this.store.dob();
      if (!answers) return throwError(() => new Error('No address to quote.'));
      if (!dob) return throwError(() => new Error('No date of birth to quote.'));
      const { address, contact } = answers;
      if (!contact.tcpaConsent) return throwError(() => new Error('No TCPA consent to quote.'));
      const navigator = this.document.defaultView?.navigator as GpcNavigator | undefined;
      const anonymousId = this.anonymousId.id;
      const aboutYou = toDotComAboutYou(contact, dob, {
        ajsAnonymousId: anonymousId,
        optOutGpcTracking: navigator?.globalPrivacyControl ?? false,
        languagePreference: navigator?.language,
      });

      return this.http.post<DotComAddressResponse>(this.addressesUrl, toDotComAddress(address, anonymousId)).pipe(
        map(response => {
          const token = response.authentication?.token;
          if (!token) throw new Error(`dot-com returned no quote token (redirect_to: ${response.redirect_to}).`);
          return token;
        }),
        switchMap(token => this.saveAboutYou(aboutYou, token).pipe(map(() => this.linkFor(token, anonymousId)))),
      );
    });
  }

  /** `POST /pages` with the about-you answers */
  private saveAboutYou(aboutYou: DotComAboutYou, token: string): Observable<DotComPagesResponse> {
    const body: DotComPagesRequest = { customerResponse: aboutYou, path: ABOUT_YOU_PATH };
    return this.http.post<DotComPagesResponse>(this.pagesUrl, body, { headers: bearer(token) });
  }

  /** `session` is the anonymous ID, left out when the browser has none */
  private linkFor(token: string, anonymousId: string): string {
    const query = new URLSearchParams({ token, ...(anonymousId && { session: anonymousId }), ...DIRECT_QUOTE_UTM });
    return `${this.env.quotingUrl}${DIRECT_QUOTE_ROUTE}?${query}`;
  }
}

function bearer(token: string) {
  return { Authorization: `Bearer ${token}` };
}
